import React, { useContext, useState } from 'react'
import { Tooltip, FloatButton, Empty, Button, Typography } from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper/PageHeaderWrapper'
import DocumentationLink from '../../../components/Misc/DocumentationLink'
import CustomReportActions from './components/CustomReportActions'
import PageHeaderExtra from './components/PageHeaderExtra'
import CustomReportGrid from './components/CustomReportGrid'
import { CustomReport } from '../../../redux/context/customReports/types'
import { CustomReportSectionsType } from '../../../redux/context/customReports/typesSection'
import CustomReportModal from './components/CustomReportModal'
import SectionModal from './components/section/CustomReportSectionModal'
import Loading from '../../../shared/components/Loading'
import { LayoutsContextType, LayoutsContext } from './components/layout/LayoutContext'
import LayoutButtons from './components/layout/LayoutButtons'
import { useCtxCustomReportStructure } from '../../../features/customReport/queries/structure/useCtxCustomReportStructure'
import { GridExportProvider } from './components/grid/GridExportContext'
import { useCalculatedReport } from './components/CalculatedReportContext'
import CtxPrintPreview from './components/print/CtxPrintPreview'

const EMPTY_REPORT: CustomReport = {
  id: 0,
  name: '',
  sections: [],
  style: { orientation: 'portrait' },
  order: 0
}

const CustomReportsPage: React.FC = () => {
  const { t } = useTranslation()
  const {
    data: calculatedReport,
    isPending: reportIsPending,
    isFetching: reportIsFetching,
    error: reportError,
    refetch: refetchReport
  } = useCalculatedReport()
  const [reportModalVisible, setreportModalVisible] = useState(false)
  const [sectionModalVisible, setSectionModalVisible] = useState(false)
  const [editSectionData, setEditSectionData] = useState<CustomReportSectionsType | null>(null)
  const { data: customReportTree, isPending: treeIsPending } = useCtxCustomReportStructure()
  const [reportInEdit, setReportInEdit] = useState<CustomReport | null>(null)
  const { layoutEdit, layouts } = useContext(LayoutsContext) as LayoutsContextType

  const handleCancel = () => {
    setReportInEdit(null)
    setreportModalVisible(false)
  }

  const openEdit = (report: CustomReport) => {
    setReportInEdit(report)
    setreportModalVisible(true)
  }

  const editModal = (editSection: CustomReportSectionsType | null) => {
    setEditSectionData(editSection)
    setSectionModalVisible(true)
  }

  const renderContent = () => {
    if ((reportIsPending && reportIsFetching) || treeIsPending) return <Loading />

    if (reportError) {
      return (
        <div style={{ paddingTop: '15vh' }}>
          <Empty
            image={
              <Typography.Text type="secondary">
                <FileTextOutlined style={{ fontSize: 48 }} />
              </Typography.Text>
            }
            styles={{ image: { height: 60 } }}
            description={t('error:error-try-again')}
          >
            <Button onClick={() => refetchReport()}>{t('global:retry')}</Button>
          </Empty>
        </div>
      )
    }

    if (!calculatedReport && customReportTree?.length === 0) {
      return (
        <div style={{ paddingTop: '15vh' }}>
          <Empty
            image={
              <Typography.Text type="secondary">
                <FileTextOutlined style={{ fontSize: 48 }} />
              </Typography.Text>
            }
            styles={{ image: { height: 60 } }}
            description={t('customReportPage:noReports')}
          >
            <Button type="primary" onClick={() => setreportModalVisible(true)}>
              {t('dashboardPage:createNow')}
            </Button>
          </Empty>
        </div>
      )
    }

    if (!calculatedReport) return <Loading />

    return (
      <>
        <CustomReportGrid calculatedReport={calculatedReport} />
        <FloatButton.BackTop visibilityHeight={1000} />
      </>
    )
  }

  // Count table sections for grid registration tracking
  const expectedGridCount = calculatedReport?.sections?.filter(section => section.type === 'table').length || 0

  return (
    <GridExportProvider expectedGridCount={expectedGridCount}>
      <CustomReportModal onClose={handleCancel} report={reportInEdit} visible={reportModalVisible} />
      {sectionModalVisible && (
        <SectionModal
          visible={sectionModalVisible}
          setVisible={setSectionModalVisible}
          key="add-section"
          reportId={calculatedReport?.id}
          section={editSectionData || null}
        />
      )}
      <PageHeaderWrapper
        fixedHeader
        affixProps={{ offsetTop: 46 }}
        title={t('customReportPage:custom-reports')}
        subTitle={
          <Tooltip placement="right" title="info">
            <DocumentationLink />
          </Tooltip>
        }
        ghost={false}
        extra={
          layoutEdit
            ? [<LayoutButtons key="layout" />]
            : [
                <PageHeaderExtra key="extra" />,
                <CtxPrintPreview
                  key="print"
                  report={calculatedReport ?? EMPTY_REPORT}
                  disabled={!calculatedReport}
                  layouts={layouts}
                />,
                <CustomReportActions
                  key="actions"
                  editModal={editModal}
                  openEdit={openEdit}
                  setreportModalVisible={setreportModalVisible}
                />
              ]
        }
      >
        {renderContent()}
      </PageHeaderWrapper>
    </GridExportProvider>
  )
}

export default CustomReportsPage
